import React from 'react';
import { Layers, X, Check } from 'lucide-react';
import { CostCenterExportMode } from '../../lib/batchSimulatorStorageBridge';

export interface CostCenterExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemsCount: number;
  exportMode: CostCenterExportMode;
  onModeChange: (mode: CostCenterExportMode) => void;
  onConfirm: () => Promise<void> | void;
  isExporting: boolean;
}

export default function CostCenterExportModal({
  isOpen,
  onClose,
  itemsCount,
  exportMode,
  onModeChange,
  onConfirm,
  isExporting,
}: CostCenterExportModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-[28px] max-w-xl w-full border border-slate-200 shadow-2xl overflow-hidden p-6 sm:p-7 space-y-5 animate-in fade-in zoom-in-95 duration-150">
        
        {/* Cabeçalho */}
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl bg-indigo-100 text-indigo-900 flex items-center justify-center">
              <Layers className="w-5 h-5 text-indigo-700" />
            </div>
            <div>
              <h3 className="text-sm font-black text-slate-900">
                Exportar para o Centro de Custos R-MAX
              </h3>
              <p className="text-[11px] text-slate-500">
                Escolha como integrar os {itemsCount} itens no seu catálogo ativo
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isExporting}
            className="p-1 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Seleção do Modo de Exportação */}
        <div className="space-y-3">
          <label className="text-xs font-bold text-slate-700 block">
            Selecione a política de preços para o catálogo:
          </label>

          {/* Opção 1: Seguro (Recomendado) */}
          <div
            onClick={() => onModeChange('original')}
            className={`p-4 rounded-2xl border-2 transition-all cursor-pointer flex items-start gap-3 ${
              exportMode === 'original'
                ? 'bg-emerald-50/60 border-emerald-500'
                : 'bg-white border-slate-200 hover:border-slate-300'
            }`}
          >
            <input
              type="radio"
              name="exportModeOption"
              checked={exportMode === 'original'}
              onChange={() => onModeChange('original')}
              className="mt-1 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
            />
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <strong className="text-xs font-black text-slate-900">
                  Modo Seguro (Recomendado): Custos e Preços Originais
                </strong>
                <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase bg-emerald-100 text-emerald-800">
                  Padrão Seguro
                </span>
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed">
                Mantém intactos os preços de venda atuais cadastrados e custos históricos no seu Centro de Custos. Os cenários da Reforma (CBS/IBS, créditos e novo preço sugerido) são arquivados de forma protegida nos metadados fiscais dos itens.
              </p>
            </div>
          </div>

          {/* Opção 2: Novo Preço Sugerido */}
          <div
            onClick={() => onModeChange('simulated')}
            className={`p-4 rounded-2xl border-2 transition-all cursor-pointer flex items-start gap-3 ${
              exportMode === 'simulated'
                ? 'bg-indigo-50/60 border-indigo-500'
                : 'bg-white border-slate-200 hover:border-slate-300'
            }`}
          >
            <input
              type="radio"
              name="exportModeOption"
              checked={exportMode === 'simulated'}
              onChange={() => onModeChange('simulated')}
              className="mt-1 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
            />
            <div className="space-y-1">
              <strong className="text-xs font-black text-slate-900">
                Modo Atualizado: Aplicar Novo Preço Sugerido (Pós-Reforma)
              </strong>
              <p className="text-[11px] text-slate-600 leading-relaxed">
                Substitui o preço de venda dos itens no Centro de Custos pelo <strong>Novo Preço Sugerido</strong> calculado pela simulação da CBS e IBS (arredondado comercialmente nos degraus de R$ 5,00 para cima).
              </p>
            </div>
          </div>
        </div>

        {/* Botões M3 */}
        <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            disabled={isExporting}
            className="px-4 py-2.5 text-xs font-bold text-slate-600 hover:text-slate-900 transition-colors cursor-pointer"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isExporting}
            className="px-6 py-2.5 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition-all shadow-md flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {isExporting ? (
              <span>Exportando...</span>
            ) : (
              <>
                <Check className="w-4 h-4 text-emerald-400" />
                <span>Confirmar Exportação</span>
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
}
